import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import colors from '../styles/colors';
import { useTranslation } from 'react-i18next';
import { menuData } from '../data/menuData';
import { useMultiLanguage } from '../hooks/useMultiLanguage';
import logo from '../assets/images/logo.png';

const PrintStyles = createGlobalStyle`
  @page { size: A4; margin: 8mm; }
  @media print {
    body { 
      -webkit-print-color-adjust: exact; 
      print-color-adjust: exact; 
      font-size: 11px;
    }
    .no-print { display: none !important; }
    .page-break { page-break-after: always; }
    section { border: none !important; }
  }
`;

const Wrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  color: ${colors.text.primary};
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
`;

const HeaderBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 8px 0;
  padding: 5px 0;
  border-bottom: 2px solid ${colors.primary.green};
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0;
  color: ${colors.primary.green};
`;

const Logo = styled.img`
  height: 36px;
`;

const PrintBtn = styled.button`
  padding: 5px 10px;
  border: 2px solid ${colors.primary.green};
  background: #fff;
  color: ${colors.primary.green};
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  &:hover {
    background: ${colors.primary.green};
    color: #fff;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  
  @media print {
    grid-template-columns: 1fr 1fr;
  }
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid ${colors.primary.green}30;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 7px;
  break-inside: avoid;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5px;
  h2 { 
    color: ${colors.primary.green}; 
    margin: 0; 
    font-size: 13px;
    font-weight: 700;
  }
  p { 
    color: ${colors.text.secondary}; 
    margin: 0; 
    font-size: 10px; 
  }
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 5px;
  padding: 3.5px 0;
  border-bottom: 1px dashed #e3e3e3;
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.div`
  font-weight: 700;
  font-size: 11px;
  line-height: 1.3;
`;

const ItemDesc = styled.div`
  color: ${colors.text.secondary};
  font-size: 9.5px;
  margin-top: 1px;
  line-height: 1.3;
`;

const ItemDescSecondary = styled.div`
  color: ${colors.text.secondary}B3;
  font-size: 8.5px;
  font-style: italic;
  line-height: 1.2;
`;

const ItemPrice = styled.div`
  font-weight: 800;
  white-space: nowrap;
  color: ${colors.primary.green};
  font-size: 11px;
`;

// IDs dos itens selecionados
const selectedItems = {
  petiscos: [
    'porcao-picanha',               // Porção de Picanha
    'porcao-coracao-galinha',       // Porção Coração de Galinha
    'porcao-asinhas-frango',        // Porção de Asinhas de Frango
    'coxinhas',                     // Coxinhas
    'bolinha-queijo',               // Bolinha de Queijo
    'croquete-presunto-queijo',     // Croquete Presunto e Queijo
    'pao-queijo',                   // Pão de Queijo
    'polenta-frita',                // Polenta Frita
    'mix-pasteis',                  // Pastéis
    'mandioca-frita'                // Mandioca Frita
  ],
  sobremesas: [
    'mousse-chocolate',             // Mousse de Chocolate
    'mousse-maracuja'               // Mousse de Maracujá
  ],
  bebidas: [
    'acqua',                        // Água Natural
    'coca-cola',                    // Coca-Cola
    'sprite',                       // Sprite
    'fanta'                         // Fanta
  ],
  cervejas: [
    'moretti-66',                   // Moretti Grande
    'moretti-33',                   // Moretti Pequena
    'heineken-66',                  // Heineken Grande
    'heineken-33',                  // Heineken Pequena
    'becks-33',                     // Becks Pequena
    'corona'                        // Corona Pequena
  ],
  drinks: [
    'spritz-aperol',                // Aperol Spritz
    'caipirinha',                   // Caipirinha
    'spritz-campari',               // Aperol Campari (Spritz Campari)
    'cachaca'                       // Chupito (Cachaça)
  ]
};

const RenderCustomSection = ({ t, getTranslations, keyName, titleKey, itemIds, customItems = {} }) => {
  const block = menuData[keyName];
  if (!block) return null;

  // Filtrar apenas os itens selecionados
  const filteredItems = block.items.filter(item => itemIds.includes(item.id));
  if (filteredItems.length === 0) return null;

  return (
    <Section>
      <SectionHeader>
        <h2>{t(titleKey, { defaultValue: block.title })}</h2>
      </SectionHeader>
      {filteredItems.map((it) => {
        // Verificar se há customização para este item
        const customItem = customItems[it.id];
        
        if (customItem) {
          // Usar dados customizados
          return (
            <ItemRow key={`${keyName}-${it.id}`}>
              <div>
                <ItemName>{customItem.name}</ItemName>
                {customItem.description && (
                  <ItemDesc>{customItem.description}</ItemDesc>
                )}
              </div>
              <ItemPrice>{it.price}</ItemPrice>
            </ItemRow>
          );
        }
        
        // Usar traduções normais
        const name = getTranslations(it.id, 'name');
        const desc = getTranslations(it.id, 'description');
        return (
          <ItemRow key={`${keyName}-${it.id}`}>
            <div>
              <ItemName>{name.mainText || it.name}</ItemName>
              {(desc.mainText || it.description) && (
                <ItemDesc>{desc.mainText || it.description}</ItemDesc>
              )}
              {desc.secondaryTexts.map((s, idx) => (
                <ItemDescSecondary key={idx}>{s.text}</ItemDescSecondary>
              ))}
            </div>
            <ItemPrice>{it.price}</ItemPrice>
          </ItemRow>
        );
      })}
    </Section>
  );
};

const PrintableCustomMenu = () => {
  const { t, i18n } = useTranslation();
  const { getTranslations } = useMultiLanguage();
  const currentLang = i18n.language;

  return (
    <Wrapper>
      <PrintStyles />
      <HeaderBar>
        <Logo src={logo} alt="Cuore Brasiliano" />
        <Title>{currentLang === 'it' ? 'Menu Speciale' : 'Cardápio Especial'}</Title>
        <PrintBtn className="no-print" onClick={() => window.print()}>
          {t('menu.print', { defaultValue: 'Imprimir' })}
        </PrintBtn>
      </HeaderBar>

      <ContentGrid>
        <div>
          {/* Porções Brasileiras / Porzioni Brasiliane */}
          <RenderCustomSection 
            t={t} 
            getTranslations={getTranslations} 
            keyName="petiscos" 
            titleKey="menu.petiscos"
            itemIds={selectedItems.petiscos}
          />

          {/* Cervejas / Birre */}
          <RenderCustomSection 
            t={t} 
            getTranslations={getTranslations} 
            keyName="cervejas" 
            titleKey="menu.cervejas"
            itemIds={selectedItems.cervejas}
          />
        </div>

        <div>
          {/* Refrigerantes / Bibite */}
          <RenderCustomSection 
            t={t} 
            getTranslations={getTranslations} 
            keyName="bebidas" 
            titleKey="menu.bebidas"
            itemIds={selectedItems.bebidas}
          />

          {/* Drinks */}
          <RenderCustomSection 
            t={t} 
            getTranslations={getTranslations} 
            keyName="drinks" 
            titleKey="menu.drinks"
            itemIds={selectedItems.drinks}
          />

          {/* Doces / Dolci */}
          <RenderCustomSection 
            t={t} 
            getTranslations={getTranslations} 
            keyName="sobremesas" 
            titleKey="menu.sobremesas"
            itemIds={selectedItems.sobremesas}
          />
        </div>
      </ContentGrid>
    </Wrapper>
  );
};

export default PrintableCustomMenu;

